import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Play, ExternalLink, CheckCircle2, AlertCircle, XCircle, Youtube, Video, Search, RefreshCw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import api from "@/lib/api";

const LIVES_RUBRIQUE_ID = "61b18e64-ce6f-48fa-a568-85b65fffebab";

const VIDEO_CATEGORIES = [
  { value: "live", label: "Live" },
  { value: "video", label: "Vidéo" },
  { value: "resource", label: "Ressource" },
];

function isValidYoutubeUrl(url) {
  if (!url) return false;
  return /^https?:\/\/(www\.)?(youtube\.com\/(watch\?v=|playlist\?list=|embed\/)|youtu\.be\/)/.test(url);
}

export default function MediathequeAdmin() {
  const [lives, setLives] = useState([]);
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingLives, setLoadingLives] = useState(true);
  const [loadingVideos, setLoadingVideos] = useState(true);
  const [searchLives, setSearchLives] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", youtube_url: "", description: "", category: "live" });
  const [verifying, setVerifying] = useState(false);
  const [verifyResults, setVerifyResults] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterTheme, setFilterTheme] = useState("all");

  const loadLives = useCallback(async () => {
    try {
      const res = await api.get(`/articles?rubrique=${LIVES_RUBRIQUE_ID}&admin=true&status=all&limit=200`);
      setLives(res.data.articles || []);
    } catch { toast.error("Erreur chargement des lives"); }
    setLoadingLives(false);
  }, []);

  const loadVideos = useCallback(async () => {
    try {
      const res = await api.get("/videos");
      setVideos(res.data || []);
    } catch { toast.error("Erreur chargement des vidéos"); }
    setLoadingVideos(false);
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const res = await api.get("/categories");
      setCategories(res.data || []);
    } catch {}
  }, []);

  useEffect(() => { loadLives(); loadVideos(); loadCategories(); }, [loadLives, loadVideos, loadCategories]);

  // Stats based on URL format check (before verification)
  const livesWithYoutube = lives.filter(l => isValidYoutubeUrl(l.video_url));
  const livesWithoutYoutube = lives.filter(l => !isValidYoutubeUrl(l.video_url));

  // Verify YouTube links via backend
  const handleVerifyAll = async () => {
    setVerifying(true);
    setVerifyResults(null);
    try {
      const res = await api.post("/verify-youtube-links");
      setVerifyResults(res.data);
      const { ok, broken, no_url, error } = res.data;
      if (broken > 0 || error > 0) {
        toast.warning(`${ok} liens OK, ${broken} cassés, ${no_url} absents, ${error} erreurs`);
      } else {
        toast.success(`Tous les ${ok} liens YouTube sont valides !`);
      }
    } catch {
      toast.error("Erreur lors de la vérification");
    }
    setVerifying(false);
  };

  // Get verification status for a specific live
  const getVerifyStatus = (liveId) => {
    if (!verifyResults) return null;
    return verifyResults.results?.find(r => r.id === liveId)?.status || null;
  };

  // Filter lives
  const filteredLives = lives.filter(l => {
    const matchSearch = l.title?.toLowerCase().includes(searchLives.toLowerCase());
    // Theme filter
    const matchTheme = filterTheme === "all" || (l.categories || []).includes(filterTheme);
    // Status filter
    let matchStatus = true;
    if (filterStatus === "with_youtube") matchStatus = isValidYoutubeUrl(l.video_url);
    else if (filterStatus === "without_youtube") matchStatus = !isValidYoutubeUrl(l.video_url);
    else if (filterStatus === "broken" && verifyResults) {
      const status = getVerifyStatus(l.id);
      matchStatus = status === "broken" || status === "error";
    }
    return matchSearch && matchTheme && matchStatus;
  });

  // Count lives per theme
  const themeCounts = categories.reduce((acc, cat) => {
    acc[cat.id] = lives.filter(l => (l.categories || []).includes(cat.id)).length;
    return acc;
  }, {});

  // Video CRUD
  const openNewVideo = () => {
    setEditing(null);
    setForm({ title: "", youtube_url: "", description: "", category: "live" });
    setDialogOpen(true);
  };
  const openEditVideo = (video) => {
    setEditing(video);
    setForm({ title: video.title, youtube_url: video.youtube_url, description: video.description || "", category: video.category || "live" });
    setDialogOpen(true);
  };
  const handleSubmitVideo = async (e) => {
    e.preventDefault();
    if (!form.title || !form.youtube_url) { toast.error("Titre et URL YouTube requis"); return; }
    try {
      if (editing) {
        await api.put(`/videos/${editing.id}`, form);
        toast.success("Vidéo mise à jour");
      } else {
        await api.post("/videos", form);
        toast.success("Vidéo ajoutée");
      }
      setDialogOpen(false);
      loadVideos();
    } catch { toast.error("Erreur lors de la sauvegarde"); }
  };
  const handleDeleteVideo = async (id) => {
    if (!window.confirm("Supprimer cette vidéo ?")) return;
    try {
      await api.delete(`/videos/${id}`);
      toast.success("Vidéo supprimée");
      loadVideos();
    } catch { toast.error("Erreur"); }
  };

  const renderStatusBadge = (live) => {
    const vStatus = getVerifyStatus(live.id);
    // If verification has been run, use those results
    if (vStatus) {
      if (vStatus === "ok") return (
        <a href={live.video_url} target="_blank" rel="noopener noreferrer" data-testid={`youtube-link-${live.id}`}>
          <Badge variant="outline" className="gap-1 text-green-700 border-green-300 bg-green-50 cursor-pointer">
            <CheckCircle2 className="h-3 w-3" /> Vérifié
          </Badge>
        </a>
      );
      if (vStatus === "broken") return (
        <Badge variant="outline" className="gap-1 text-red-700 border-red-300 bg-red-50" data-testid={`broken-link-${live.id}`}>
          <XCircle className="h-3 w-3" /> Lien cassé
        </Badge>
      );
      if (vStatus === "error") return (
        <Badge variant="outline" className="gap-1 text-red-700 border-red-300 bg-red-50" data-testid={`error-link-${live.id}`}>
          <AlertCircle className="h-3 w-3" /> Erreur
        </Badge>
      );
      if (vStatus === "no_url") return (
        <Badge variant="outline" className="gap-1 text-amber-700 border-amber-300 bg-amber-50" data-testid={`no-youtube-${live.id}`}>
          <AlertCircle className="h-3 w-3" /> Pas de lien
        </Badge>
      );
    }
    // Default: use URL format check
    if (isValidYoutubeUrl(live.video_url)) return (
      <a href={live.video_url} target="_blank" rel="noopener noreferrer" data-testid={`youtube-link-${live.id}`}>
        <Badge variant="outline" className="gap-1 text-green-700 border-green-300 bg-green-50 cursor-pointer">
          <CheckCircle2 className="h-3 w-3" /> YouTube
        </Badge>
      </a>
    );
    return (
      <Badge variant="outline" className="gap-1 text-amber-700 border-amber-300 bg-amber-50" data-testid={`no-youtube-${live.id}`}>
        <AlertCircle className="h-3 w-3" /> Pas de lien
      </Badge>
    );
  };

  return (
    <div data-testid="mediatheque-admin">
      <div className="mb-8">
        <h1 className="font-heading text-3xl">Médiathèque</h1>
        <p className="text-muted-foreground mt-1">Gérez vos OFA Lives et vidéos importées</p>
      </div>

      <Tabs defaultValue="lives" className="space-y-6">
        <TabsList data-testid="mediatheque-tabs">
          <TabsTrigger value="lives" data-testid="tab-lives" className="gap-2">
            <Youtube className="h-4 w-4" /> OFA Lives ({lives.length})
          </TabsTrigger>
          <TabsTrigger value="videos" data-testid="tab-videos" className="gap-2">
            <Video className="h-4 w-4" /> Vidéos ({videos.length})
          </TabsTrigger>
        </TabsList>

        {/* OFA LIVES TAB */}
        <TabsContent value="lives" className="space-y-4">
          {/* Stats bar */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="border rounded-lg p-4 bg-white" data-testid="lives-total-stat">
              <p className="text-sm text-muted-foreground">Total Lives</p>
              <p className="text-2xl font-heading">{lives.length}</p>
            </div>
            <div className="border rounded-lg p-4 bg-white" data-testid="lives-youtube-stat">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <p className="text-sm text-muted-foreground">Lien YouTube valide</p>
              </div>
              <p className="text-2xl font-heading text-green-700">
                {verifyResults ? verifyResults.ok : livesWithYoutube.length}
              </p>
            </div>
            <div className="border rounded-lg p-4 bg-white" data-testid="lives-no-youtube-stat">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <p className="text-sm text-muted-foreground">
                  {verifyResults ? "Problèmes détectés" : "Sans lien YouTube"}
                </p>
              </div>
              <p className="text-2xl font-heading text-amber-700">
                {verifyResults ? (verifyResults.broken + verifyResults.no_url + verifyResults.error) : livesWithoutYoutube.length}
              </p>
            </div>
          </div>

          {/* Verify bar */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-4 border rounded-lg bg-slate-50">
            <Button
              onClick={handleVerifyAll}
              disabled={verifying || loadingLives}
              variant={verifyResults ? "outline" : "default"}
              className="gap-2"
              data-testid="verify-youtube-btn"
            >
              {verifying ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Vérification en cours...</>
              ) : verifyResults ? (
                <><RefreshCw className="h-4 w-4" /> Revérifier les liens</>
              ) : (
                <><RefreshCw className="h-4 w-4" /> Vérifier tous les liens YouTube</>
              )}
            </Button>
            {verifyResults && (
              <div className="flex items-center gap-3 text-sm">
                <span className="text-green-700 font-medium">{verifyResults.ok} OK</span>
                {verifyResults.broken > 0 && <span className="text-red-700 font-medium">{verifyResults.broken} cassés</span>}
                {verifyResults.no_url > 0 && <span className="text-amber-700 font-medium">{verifyResults.no_url} absents</span>}
                {verifyResults.error > 0 && <span className="text-red-600 font-medium">{verifyResults.error} erreurs</span>}
              </div>
            )}
          </div>

          {/* Search + filter */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher un live..."
                value={searchLives}
                onChange={(e) => setSearchLives(e.target.value)}
                className="pl-9"
                data-testid="lives-search-input"
              />
            </div>
            <Select value={filterTheme} onValueChange={setFilterTheme}>
              <SelectTrigger className="w-[260px]" data-testid="lives-theme-filter">
                <SelectValue placeholder="Filtrer par thème" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les thèmes ({lives.length})</SelectItem>
                {categories.map(cat => (
                  themeCounts[cat.id] > 0 && (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name} ({themeCounts[cat.id]})
                    </SelectItem>
                  )
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[200px]" data-testid="lives-filter-select">
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous ({lives.length})</SelectItem>
                <SelectItem value="with_youtube">Avec YouTube ({livesWithYoutube.length})</SelectItem>
                <SelectItem value="without_youtube">Sans YouTube ({livesWithoutYoutube.length})</SelectItem>
                {verifyResults && (verifyResults.broken + verifyResults.error) > 0 && (
                  <SelectItem value="broken">Liens cassés ({verifyResults.broken + verifyResults.error})</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Lives table */}
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Titre</TableHead>
                  <TableHead>Thème</TableHead>
                  <TableHead>Lien YouTube</TableHead>
                  <TableHead>Vues</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingLives ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8">Chargement...</TableCell></TableRow>
                ) : filteredLives.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Aucun live trouvé</TableCell></TableRow>
                ) : (
                  filteredLives.map((live) => (
                    <TableRow key={live.id} data-testid={`live-row-${live.id}`}>
                      <TableCell>
                        <div className="max-w-[280px]">
                          <Link to={`/admin/articles/${live.id}/edit`} className="font-medium hover:text-primary text-sm line-clamp-1">
                            {live.title}
                          </Link>
                          <p className="text-xs text-muted-foreground font-mono mt-0.5 truncate">/{live.slug}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(live.category_details || []).map(cat => (
                            <span key={cat.id} className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded truncate max-w-[120px]" title={cat.name}>
                              {cat.name?.split("&")[0]?.trim()}
                            </span>
                          ))}
                          {(!live.category_details || live.category_details.length === 0) && (
                            <span className="text-[10px] text-muted-foreground">—</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{renderStatusBadge(live)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{live.views || 0}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {live.publication_date ? new Date(live.publication_date).toLocaleDateString("fr-FR") : "\u2014"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {isValidYoutubeUrl(live.video_url) && (
                            <a href={live.video_url} target="_blank" rel="noopener noreferrer">
                              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`open-youtube-${live.id}`}>
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            </a>
                          )}
                          <Link to={`/admin/articles/${live.id}/edit`}>
                            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`edit-live-${live.id}`}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </Link>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* VIDEOS TAB */}
        <TabsContent value="videos" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={openNewVideo} data-testid="add-video-btn">
              <Plus className="h-4 w-4 mr-2" /> Ajouter une vidéo
            </Button>
          </div>

          {loadingVideos ? (
            <div className="space-y-4">{[1, 2, 3].map((i) => (<div key={i} className="h-20 bg-muted animate-pulse rounded" />))}</div>
          ) : videos.length === 0 ? (
            <div className="text-center py-20 border border-dashed rounded-lg">
              <Play className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="font-heading text-xl mb-2">Aucune vidéo</h3>
              <p className="text-muted-foreground mb-6">Ajoutez votre première vidéo YouTube</p>
              <Button onClick={openNewVideo} data-testid="first-video-btn"><Plus className="h-4 w-4 mr-2" /> Ajouter une vidéo</Button>
            </div>
          ) : (
            <div className="space-y-3">
              {videos.map((video) => (
                <div key={video.id} className="flex items-center gap-4 p-4 border rounded-lg hover:bg-accent/50 transition-colors" data-testid={`video-item-${video.id}`}>
                  <div className="w-32 h-20 rounded overflow-hidden shrink-0 bg-muted">
                    {video.thumbnail ? (
                      <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Play className="h-6 w-6 text-muted-foreground" /></div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium truncate">{video.title}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full">
                        {VIDEO_CATEGORIES.find((c) => c.value === video.category)?.label || video.category}
                      </span>
                      {video.description && <span className="text-xs text-muted-foreground truncate">{video.description}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <a href={video.youtube_url} target="_blank" rel="noopener noreferrer">
                      <Button variant="ghost" size="icon"><ExternalLink className="h-4 w-4" /></Button>
                    </a>
                    <Button variant="ghost" size="icon" onClick={() => openEditVideo(video)} data-testid={`edit-video-${video.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDeleteVideo(video.id)} data-testid={`delete-video-${video.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Video Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Modifier la vidéo" : "Ajouter une vidéo"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitVideo} className="space-y-4">
            <div>
              <Label>Titre</Label>
              <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Titre de la vidéo" className="mt-1" required data-testid="video-title-input" />
            </div>
            <div>
              <Label>URL YouTube</Label>
              <Input value={form.youtube_url} onChange={(e) => setForm({...form, youtube_url: e.target.value})} placeholder="https://www.youtube.com/watch?v=..." className="mt-1" required data-testid="video-url-input" />
            </div>
            <div>
              <Label>Catégorie</Label>
              <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                <SelectTrigger className="mt-1" data-testid="video-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {VIDEO_CATEGORIES.map((c) => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description (optionnelle)</Label>
              <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Courte description..." className="mt-1" data-testid="video-description-input" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button type="submit" data-testid="video-save-btn">{editing ? "Mettre à jour" : "Ajouter"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
